import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { TimelineShift, ShiftTemplate } from '@wilson/interfaces';
import {
  CreateNewViaDragAndDropService,
  getDateFromPixelPosition,
} from '@wilson/shift-timeline/services';
import {
  AddShiftTemplateToSuggestionList,
  ShiftTimelineDataState,
  ShiftTimelineDataTransformerService,
  ShiftTimelineState,
  TimelineAssignedShiftValidationsStateService,
  TimelineCreateNewShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
  TimelineUpsertNewShiftV2,
} from '@wilson/shift-timeline/state';
import { ShiftTemplateHelperService } from '../../components/shift-template-drop-zone/shift-template-helper.service';

import { firstValueFrom } from 'rxjs';
import { ShiftTemplateService } from '@wilson/shift-templates';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import {
  dateToPositionPx,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { toDatabaseDateFormat } from '@wilson/localization';

@Injectable()
export class ShiftTemplateDropZoneHelperService {
  private store = inject(Store);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly shiftTemplateService = inject(ShiftTemplateService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly shiftTemplateHelperService = inject(
    ShiftTemplateHelperService,
  );
  private readonly shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );
  private shiftMultiSelectHelperService = inject(ShiftMultiSelectHelperService);
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );

  handleOnDragOverProcess({
    offsetX,
    userId,
  }: {
    offsetX: number;
    userId: string | null | undefined;
  }): {
    shiftStartDate: Date;
    mockShiftWithoutActivities: TimelineShift | null;
  } | null {
    const shiftStartDate = getDateFromPixelPosition({
      pixelPosition: offsetX,
      timeframe: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
      frameLength: this.store.selectSnapshot(ShiftTimelineState.frameLength),
    });

    if (userId) {
      const mockShift = this.shiftTemplateHelperService.getShiftOfShiftTemplate(
        userId,
        toDatabaseDateFormat(shiftStartDate),
      );
      let mockShiftWithoutActivities: TimelineShift | null = null;
      if (mockShift) {
        mockShiftWithoutActivities =
          this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
            mockShift,
          );
        this.store.dispatch(
          new TimelineUpsertNewShiftV2({
            shift: mockShiftWithoutActivities,
          }),
        );
      }

      return {
        shiftStartDate,
        mockShiftWithoutActivities,
      };
    } else {
      return null;
    }
  }

  async handleOnDropProcess({
    shiftTemplate,
    shiftStartDate,
    userId,
    mockShiftWithoutActivities,
  }: {
    shiftTemplate: ShiftTemplate | null;
    shiftStartDate: Date;
    userId: string | undefined;
    mockShiftWithoutActivities: TimelineShift | null;
  }): Promise<void> {
    const startDate = toDatabaseDateFormat(shiftStartDate);

    if (userId && shiftTemplate?.id) {
      const tempShiftId = crypto.randomUUID();
      const tempShift = this.shiftTemplateHelperService.getShiftOfShiftTemplate(
        userId,
        startDate,
      );
      if (mockShiftWithoutActivities && tempShift) {
        try {
          this.store.dispatch(
            new TimelineRemoveShiftFromUserV2({
              userId: userId,
              shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            }),
          );
          this.store.dispatch(
            new TimelineCreateNewShiftV2(
              {
                ...mockShiftWithoutActivities,
                id: tempShiftId,
              },
              userId,
            ),
          );
          this.createNewViaDragAndDropService.addUpdatingId(tempShiftId);
          const shiftTemplate = await firstValueFrom(
            this.shiftTemplateHelperService.shiftTemplate$,
          );
          if (shiftTemplate) {
            const shift =
              await this.shiftTemplateService.createShiftFromTemplate(
                shiftTemplate.id as string,
                userId,
                tempShift.startDate,
              );
            this.store.dispatch(
              new TimelineRemoveShiftFromUserV2({
                userId: userId,
                shiftId: tempShiftId,
              }),
            );

            this.store.dispatch(
              new TimelineCreateNewShiftV2(
                {
                  ...mockShiftWithoutActivities,
                  id: shift.id as string,
                },
                userId,
              ),
            );
            this.handleDrawerOpening({
              startDatetime: new Date(mockShiftWithoutActivities.startDatetime),
              endDatetime: new Date(mockShiftWithoutActivities.endDatetime),
              shiftId: shift.id as string,
            });
            this.store.dispatch(
              new AddShiftTemplateToSuggestionList(shiftTemplate),
            );
            this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
            this.timelineAssignedShiftValidationsStateService.queueValidationRequest(
              [shift.id as string],
              {
                forceRefresh: true,
              },
            );
            this.createNewViaDragAndDropService.removeUpdatingId(tempShiftId);
            this.shiftTemplateHelperService.setShiftTemplate(null);
            this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers(
              [userId as string],
            );
            this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
              [userId as string],
            );
          } else {
            console.error('Shift template not found!');
            this.resetOnError(userId, tempShiftId);
          }
        } catch {
          this.resetOnError(userId, tempShiftId);
        }
      }
    }
  }

  private handleDrawerOpening({
    startDatetime,
    endDatetime,
    shiftId,
  }: {
    startDatetime: Date;
    endDatetime: Date;
    shiftId: string;
  }): void {
    setTimeout(() => {
      const currentDemarcatorRange = {
        left: dateToPositionPx(startDatetime, {
          fromRightSide: false,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        }),
        right: dateToPositionPx(endDatetime, {
          fromRightSide: true,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        }),
        shiftDuration: null,
        adjustmentNeeded: true,
      };
      this.shiftMultiSelectHelperService.setSelectedShift([
        {
          id: shiftId,
          left: currentDemarcatorRange?.left || null,
          right: currentDemarcatorRange?.right || null,
          isUnAssignedShift: false,
        },
      ]);
    });
  }

  private resetOnError(userId: string, shiftId: string): void {
    this.store.dispatch([
      new TimelineRemoveShiftFromUserV2({
        userId,
        shiftId,
      }),
    ]);
    this.createNewViaDragAndDropService.removeUpdatingId(shiftId);
    this.nzMessageService.error(
      this.translate.instant('general.something_went_wrong'),
    );
  }
}
